import { GraduationCap, School, ClipboardCheck, Briefcase, Handshake, HeartHandshake, ArrowUpRight } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";
import { scrollToId } from "@/lib/scroll";

const SERVICES = [
  {
    icon: GraduationCap,
    title: "Student Visa Assistance",
    tag: "Subclass 500",
    desc: "End-to-end guidance through your application, documentation, and visa compliance.",
  },
  {
    icon: School,
    title: "University & College Admissions",
    tag: "Enrolment",
    desc: "Course selection and enrolment support across Australia's top institutions.",
  },
  {
    icon: ClipboardCheck,
    title: "Skills Assessment & Skilled Migration",
    tag: "189 / 190 / 491",
    desc: "Support with skills assessments and permanent residency pathways.",
  },
  {
    icon: Briefcase,
    title: "Graduate & Post-Study Work Visas",
    tag: "Subclass 485",
    desc: "Clear pathways to stay and work in Australia after you complete your studies.",
  },
  {
    icon: Handshake,
    title: "Employer-Sponsored Visas",
    tag: "Sponsored",
    desc: "Support for skilled visas sponsored by Australian employers.",
  },
  {
    icon: HeartHandshake,
    title: "Family & Partner Visas",
    tag: "Family",
    desc: "Assistance with partner, family reunification, and child visas.",
  },
];

export default function Services() {
  return (
    <section id="services" className="relative bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Chapter number="01" label="Our Services" />
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 max-w-2xl font-display text-3xl font-extrabold tracking-[-0.03em] text-slate-900 sm:text-4xl lg:text-5xl">
            How We Help You <span className="text-sky-600">Succeed</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-4 max-w-xl text-base text-slate-600 sm:text-lg">
            Six specialist services covering every stage of your journey — from your
            first application to permanent residency.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={0.08 * i} className="h-full">
              <div
                data-testid={`service-card-item-${i + 1}`}
                className="group relative flex h-full flex-col rounded-2xl border border-sky-100 bg-gradient-to-b from-white to-sky-50/50 p-7 transition-all duration-500 hover:-translate-y-2 hover:border-sky-300 hover:shadow-[0_24px_60px_rgba(2,132,199,0.16)]"
              >
                <div className="flex items-start justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-600 transition-all duration-500 group-hover:bg-sky-600 group-hover:text-white group-hover:shadow-[0_10px_24px_rgba(2,132,199,0.4)]">
                    <s.icon className="h-6 w-6" />
                  </span>
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-sky-500">
                    {s.tag}
                  </span>
                </div>
                <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-slate-900">
                  {s.title}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                <button
                  data-testid={`service-card-cta-${i + 1}`}
                  onClick={() => scrollToId("contact")}
                  className="mt-6 flex items-center gap-1.5 text-sm font-semibold text-sky-600 transition-colors hover:text-sky-700"
                >
                  Enquire about this
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <span className="pointer-events-none absolute bottom-5 right-6 font-display text-5xl font-extrabold text-sky-100/80 transition-colors duration-500 group-hover:text-sky-200">
                  0{i + 1}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
